import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Loader2 } from 'lucide-react';

interface QuestionRow {
  id: string;
  question: string;
  reponse: string | null;
  created_at: string;
  user_id: string;
}

interface UserOption {
  id: string;
  label: string;
}

export const AssistantQuestions = () => {
  const [questions, setQuestions] = useState<QuestionRow[]>([]);
  const [users, setUsers] = useState<Record<string, UserOption>>({});
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data: qData } = await supabase
        .from('questions_assistant')
        .select('id, question, reponse, created_at, user_id')
        .order('created_at', { ascending: false });

      const rows = (qData || []) as QuestionRow[];
      setQuestions(rows);

      const userIds = [...new Set(rows.map((r) => r.user_id))];
      if (userIds.length > 0) {
        const { data: profils } = await supabase
          .from('profils_utilisateur')
          .select('id, email, prenom, nom')
          .in('id', userIds);

        const map: Record<string, UserOption> = {};
        (profils || []).forEach((p: any) => {
          const name = [p.prenom, p.nom].filter(Boolean).join(' ');
          map[p.id] = {
            id: p.id,
            label: name ? `${name} (${p.email || ''})` : p.email || p.id,
          };
        });
        setUsers(map);
      }
      setLoading(false);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const list =
      selectedUser === 'all'
        ? questions
        : questions.filter((q) => q.user_id === selectedUser);
    // Par défaut : afficher les 10 dernières questions
    return selectedUser === 'all' ? list.slice(0, 10) : list;
  }, [questions, selectedUser]);

  const userList = useMemo(() => {
    const seen = new Set<string>();
    return questions
      .filter((q) => {
        if (seen.has(q.user_id)) return false;
        seen.add(q.user_id);
        return true;
      })
      .map((q) => users[q.user_id] || { id: q.user_id, label: q.user_id });
  }, [questions, users]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-nautic" />
            Questions Assistant IA
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {selectedUser === 'all'
              ? '10 dernières questions posées'
              : `${filtered.length} question(s) pour cet utilisateur`}
          </p>
        </div>
        <Select value={selectedUser} onValueChange={setSelectedUser}>
          <SelectTrigger className="w-full sm:w-72">
            <SelectValue placeholder="Filtrer par utilisateur" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous (10 dernières)</SelectItem>
            {userList.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-nautic" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground text-center py-12">
          Aucune question à afficher.
        </p>
      ) : (
        <div className="space-y-4">
          {filtered.map((q) => (
            <Card key={q.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <CardTitle className="text-base font-semibold">
                    {q.question}
                  </CardTitle>
                  <Badge variant="secondary" className="shrink-0">
                    {new Date(q.created_at).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {users[q.user_id]?.label || q.user_id}
                </p>
              </CardHeader>
              {q.reponse && (
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {q.reponse}
                  </p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
